import React, { useMemo } from 'react'
import startCase from 'lodash/startCase'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link, { LinkProps } from '@mui/material/Link'
import { Link as RouterLink } from 'react-router-dom'
import {
  ReportSection,
  GetReportDetailsQuery,
  GetReportFavoriteDetailsQuery,
} from 'gqlTypes'
import GloablSettingsMenu from './GlobalSettings/GlobalSettingsMenu'
import { useCurrentUserContext } from '../contexts/CurrentUserContext'
import ReportFavoriteLabel from './ReportFavoriteLabel'
import { Grid } from '@mui/material'

export const HEADER_HEIGHT = 104

interface LinkRouterProps extends LinkProps {
  to: string
  replace?: boolean
}
const LinkRouter = (props: LinkRouterProps) => (
  <Link {...props} component={RouterLink as any} />
)

interface Props {
  report:
    | NonNullable<GetReportDetailsQuery['report']>
    | NonNullable<GetReportFavoriteDetailsQuery['reportWithFavorites']>
  sectionId?: string
  favorites?: boolean
}
const ReportHeader: React.FC<Props> = ({
  report,
  sectionId,
  favorites = false,
}) => {
  const currentUser = useCurrentUserContext()

  const section = useMemo<ReportSection | null>(() => {
    if (!sectionId) return null
    return report.sections.find((section) => section.id === sectionId) || null
  }, [report, sectionId])

  const parentSection = useMemo<ReportSection | null>(() => {
    const parentReportSectionId = section?.parentReportSectionId
    return (
      report.sections.find((section) => section.id === parentReportSectionId) ||
      null
    )
  }, [report, section])

  const exitBetaViewUrl = currentUser?.admin
    ? `/admin/reports/${report.id}`
    : sectionId
      ? `/report_sections/${sectionId}/`
      : '/'

  const reportViewPath = favorites ? 'report_bookmarks' : 'reports'

  return (
    <>
      <Paper
        square
        sx={{
          position: 'fixed',
          width: '100%',
          px: 3,
          height: HEADER_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        <Box pt={2}>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            {currentUser && currentUser?.admin && (
              <Link href={exitBetaViewUrl}>Exit BETA</Link>
            )}
            {currentUser && !currentUser?.admin && (
              <LinkRouter to="/">My Reports</LinkRouter>
            )}
            {section && (
              <LinkRouter to={`/reports/${report.id}/`}>
                Report Overview
              </LinkRouter>
            )}
            {parentSection && (
              <LinkRouter
                to={`/${reportViewPath}/${report.id}/sections/${parentSection.id}`}
              >
                {startCase(parentSection.title?.toLowerCase())}
              </LinkRouter>
            )}
            {section ? (
              <Typography>{startCase(section.title?.toLowerCase())}</Typography>
            ) : (
              <Typography>Report Overview</Typography>
            )}
          </Breadcrumbs>
        </Box>

        <Grid container alignItems="center" justifyContent="space-between">
          <Grid
            item
            sx={{
              display: { xs: 'none', lg: 'block' },
              width: 'calc(100% - 800px)',
            }}
          >
            <Box
              sx={{
                fontWeight: 'bold',
                mt: 1,
              }}
            >
              {favorites && <ReportFavoriteLabel />}
              <Typography
                noWrap
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 'bold',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {report.title ?? '(No Title)'}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <GloablSettingsMenu reportId={report.id} />
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ height: HEADER_HEIGHT }} />
    </>
  )
}

export default ReportHeader
